// ImageGallery.jsx
// Component for displaying and navigating listing images - Modernized

import React, { useState } from 'react';
import { COLORS, LAYOUT, CARD_STYLES } from '../../constants';

// Modern SVG Icons
const ChevronLeftIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="15,18 9,12 15,6"></polyline>
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="9,18 15,12 9,6"></polyline>
  </svg>
);

const ExpandIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="15,3 21,3 21,9"></polyline>
    <polyline points="9,21 3,21 3,15"></polyline>
    <line x1="21" y1="3" x2="14" y2="10"></line>
    <line x1="3" y1="21" x2="10" y2="14"></line>
  </svg>
);

const ImageGallery = ({ images, imgIndex, onPrevious, onNext, onSelectImage }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const fullscreenRef = React.useRef(null);

  // Handle escape key and fullscreen change events
  React.useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement && !document.webkitFullscreenElement && !document.msFullscreenElement) {
        setIsFullscreen(false);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, []);

  if (!images || images.length === 0) {
    return (
      <div style={modernStyles.noImagesPlaceholder}>
        <div style={modernStyles.placeholderContent}>
          <div style={modernStyles.placeholderIcon}>📷</div>
          <span style={modernStyles.placeholderText}>No images available</span>
        </div>
      </div>
    );
  }

  const openFullscreen = () => {
    const elem = fullscreenRef.current;
    if (elem) {
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      } else if (elem.webkitRequestFullscreen) { // Safari
        elem.webkitRequestFullscreen();
      } else if (elem.msRequestFullscreen) { // IE11
        elem.msRequestFullscreen();
      }
      setIsFullscreen(true);
    }
  };

  const closeFullscreen = () => {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.webkitExitFullscreen) { // Safari
      document.webkitExitFullscreen();
    } else if (document.msExitFullscreen) { // IE11
      document.msExitFullscreen();
    }
    setIsFullscreen(false);
  };

  return (
    <>
      <div style={modernStyles.galleryContainer}>
        {/* Main Image Display */}
        <div style={modernStyles.mainImageContainer}>
          <div style={modernStyles.imageWrapper}>
            <img
              src={images[imgIndex]}
              alt={`${imgIndex + 1} of ${images.length}`}
              style={modernStyles.mainImage}
            />
            
            {/* Expand Button */}
            <button
              onClick={openFullscreen}
              style={modernStyles.expandButton}
              aria-label="View fullscreen"
            >
              <ExpandIcon />
            </button>
            
            {/* Navigation Arrows */}
            {images.length > 1 && (
              <>
                <button
                  className="image-gallery-nav"
                  onClick={onPrevious}
                  style={{...modernStyles.navButton, ...modernStyles.navButtonLeft}}
                  aria-label="Previous image"
                >
                  <ChevronLeftIcon />
                </button>
                <button
                  className="image-gallery-nav"
                  onClick={onNext}
                  style={{...modernStyles.navButton, ...modernStyles.navButtonRight}}
                  aria-label="Next image"
                >
                  <ChevronRightIcon />
                </button>
              </>
            )}
            
            {/* Image Counter */}
            {images.length > 1 && (
              <div style={modernStyles.imageCounter}>
                {imgIndex + 1} / {images.length}
              </div>
            )}
          </div>
        </div>
        
        {/* Thumbnail Strip */}
        {images.length > 1 && (
          <div style={modernStyles.thumbnailStrip}>
            <div className="thumbnail-container" style={modernStyles.thumbnailContainer}>
              {images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => onSelectImage(i)}
                  style={{
                    ...modernStyles.thumbnail,
                    ...(imgIndex === i ? modernStyles.thumbnailActive : modernStyles.thumbnailInactive)
                  }}
                  aria-label={`View image ${i + 1}`}
                >
                  <img
                    src={img}
                    alt={`Thumbnail ${i + 1}`}
                    style={modernStyles.thumbnailImage}
                  />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Fullscreen Container - Uses Native Browser Fullscreen API */}
      <div 
        ref={fullscreenRef}
        style={{
          ...modernStyles.fullscreenContainer,
          ...(isFullscreen ? modernStyles.fullscreenContainerActive : {})
        }}
      >
        {isFullscreen && (
          <>
            <button
              onClick={closeFullscreen}
              style={modernStyles.fullscreenCloseBtn}
              aria-label="Exit fullscreen"
            >
              ✕
            </button>
            
            <img
              src={images[imgIndex]}
              alt={`Fullscreen view ${imgIndex + 1} of ${images.length}`}
              style={modernStyles.fullscreenImg}
            />
            
            {images.length > 1 && (
              <div style={modernStyles.fullscreenCounter}>
                {imgIndex + 1} / {images.length}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

// Modern styles for the image gallery
const modernStyles = {
  galleryContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: LAYOUT.spacing.lg,
  },
  
  // Main Image
  mainImageContainer: {
    ...CARD_STYLES.base,
    padding: 0,
    overflow: 'hidden',
    borderRadius: LAYOUT.borderRadius.xl,
  },
  imageWrapper: {
    position: 'relative',
    width: '100%',
    paddingBottom: '75%', // 4:3 aspect ratio
    backgroundColor: COLORS.grayLight,
  },
  mainImage: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    backgroundColor: COLORS.white,
  },
  
  // Expand Button
  expandButton: {
    position: 'absolute',
    top: LAYOUT.spacing.md,
    right: LAYOUT.spacing.md,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    color: COLORS.white,
    border: 'none',
    borderRadius: LAYOUT.borderRadius.md,
    width: '44px',
    height: '44px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    zIndex: 2,
  },
  
  // Navigation
  navButton: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    backgroundColor: 'rgba(255, 255, 255, 0.98)',
    backdropFilter: 'blur(8px)',
    border: `2px solid ${COLORS.primary}`,
    borderRadius: '50%',
    width: '44px',
    height: '44px',
    cursor: 'pointer',
    color: COLORS.primary,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
    transition: 'none',
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  navButtonLeft: {
    left: LAYOUT.spacing.lg,
  },
  navButtonRight: {
    right: LAYOUT.spacing.lg,
  },
  
  // Image Counter
  imageCounter: {
    position: 'absolute',
    bottom: LAYOUT.spacing.md,
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    color: COLORS.white,
    padding: `${LAYOUT.spacing.xs} ${LAYOUT.spacing.md}`,
    borderRadius: LAYOUT.borderRadius.md,
    fontSize: '0.875rem',
    fontWeight: '500',
    zIndex: 2,
  },
  
  // Thumbnails
  thumbnailStrip: {
    ...CARD_STYLES.base,
    padding: LAYOUT.spacing.lg,
    borderRadius: LAYOUT.borderRadius.lg,
  },
  thumbnailContainer: {
    display: 'flex',
    gap: LAYOUT.spacing.sm,
    overflowX: 'auto',
    padding: `6px ${LAYOUT.spacing.sm}`,
    margin: `0 -${LAYOUT.spacing.sm}`,
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
  },
  thumbnail: {
    flex: '0 0 80px',
    height: '80px',
    border: 'none',
    borderRadius: LAYOUT.borderRadius.md,
    cursor: 'pointer',
    transition: 'opacity 0.15s ease',
    overflow: 'hidden',
    padding: 0,
    backgroundColor: 'transparent',
  },
  thumbnailImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  thumbnailActive: {
    boxShadow: `0 0 0 3px ${COLORS.primary}`,
    transform: 'scale(1.0)',
    borderRadius: LAYOUT.borderRadius.md,
  },
  thumbnailInactive: {
    opacity: 0.7,
  },
  
  // No Images Placeholder
  noImagesPlaceholder: {
    ...CARD_STYLES.base,
    width: '100%',
    paddingBottom: '75%',
    position: 'relative',
    backgroundColor: COLORS.grayLight,
    borderRadius: LAYOUT.borderRadius.xl,
  },
  placeholderContent: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    textAlign: 'center',
  },
  placeholderIcon: {
    fontSize: '3rem',
    marginBottom: LAYOUT.spacing.md,
  },
  placeholderText: {
    color: COLORS.text.muted,
    fontSize: '1rem',
    fontWeight: '500',
  },
  
  // Native Fullscreen API Styles
  fullscreenContainer: {
    display: 'none',
  },
  fullscreenContainerActive: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100vw',
    height: '100vh',
    backgroundColor: '#000',
    position: 'relative',
  },
  fullscreenImg: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
  },
  fullscreenCloseBtn: {
    position: 'absolute',
    top: '20px',
    right: '20px',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    color: '#fff',
    border: '2px solid rgba(255, 255, 255, 0.4)',
    borderRadius: '50%',
    width: '50px',
    height: '50px',
    fontSize: '24px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    zIndex: 10,
  },
  fullscreenCounter: {
    position: 'absolute',
    bottom: '30px',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    color: '#fff',
    padding: '8px 16px',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: '600',
  },
};

// Clean animations
const imageGalleryStyles = `
  .image-gallery-nav {
    transition: all 0.08s ease-out !important;
  }
  
  .image-gallery-nav:hover {
    background-color: rgba(255, 255, 255, 1) !important;
    transform: translateY(-50%) scale(1.02) !important;
    box-shadow: 0 2px 8px rgba(206, 17, 38, 0.15) !important;
  }
  
  .image-gallery-nav:active {
    transform: translateY(-50%) scale(0.96) !important;
    transition: all 0.05s ease-out !important;
  }
  
  .thumbnail-container::-webkit-scrollbar {
    display: none;
  }
  
  button[aria-label="Exit fullscreen"]:hover {
    background-color: rgba(255, 255, 255, 0.35) !important;
    border-color: rgba(255, 255, 255, 0.6) !important;
    transform: scale(1.1);
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleElement = document.getElementById('image-gallery-nav-styles');
  if (!styleElement) {
    const style = document.createElement('style');
    style.id = 'image-gallery-nav-styles';
    style.textContent = imageGalleryStyles;
    document.head.appendChild(style);
  }
}

export default ImageGallery;